import { createContext, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import {
  Edit2,
  Trash2,
  Key,
  Save,
  X,
  Power,
  User,
  Eye,
  EyeOff,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../../contexts/AuthContext'
import { userService } from '../../../services/userService'
import { User as UserType } from '../../../types'
import { ConfirmDialog, useConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { DetailLayout } from '../../../components/ui/DetailLayout'
import { TabItem } from '../../../components/ui/DetailTabs'

interface UserContextType {
  userData: UserType | null
  isLoading: boolean
  isEditing: boolean
  isSaving: boolean
  isCurrentUser: boolean
  formData: {
    name: string
    email: string
  }
  setIsEditing: (value: boolean) => void
  setFormData: (data: { name: string; email: string }) => void
  handleSave: () => Promise<void>
  handleToggleActive: () => Promise<void>
  fetchUser: () => Promise<void>
}

const UserContext = createContext<UserContextType | null>(null)

export function useUserContext() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within UserDetailLayout')
  }
  return context
}

export function UserDetailLayout() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  const [userData, setUserData] = useState<UserType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const confirmDialog = useConfirmDialog()

  const [formData, setFormData] = useState({
    name: '',
    email: '',
  })

  useEffect(() => {
    if (id) {
      fetchUser()
    }
  }, [id])

  const fetchUser = async () => {
    try {
      const data = await userService.getById(id!)
      setUserData(data)
      setFormData({
        name: data.name,
        email: data.email || '',
      })
    } catch (error) {
      toast.error('Error al cargar usuario')
      navigate('/configuration/users')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!userData) return
    setIsSaving(true)
    try {
      await userService.update(userData.id, formData)
      toast.success('Usuario actualizado')
      setIsEditing(false)
      fetchUser()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!userData || !newPassword) return
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres')
      return
    }
    setIsSaving(true)
    try {
      await userService.update(userData.id, { password: newPassword })
      toast.success('Contraseña actualizada')
      setShowPasswordModal(false)
      setNewPassword('')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar contraseña')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async () => {
    if (!userData || userData.id === currentUser?.id) return
    try {
      const updated = await userService.toggleActive(userData.id)
      toast.success(`Usuario ${updated.isActive ? 'activado' : 'desactivado'}`)
      fetchUser()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al cambiar estado')
    }
  }

  const handleDelete = async () => {
    if (!userData || userData.id === currentUser?.id) return

    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar usuario',
      message: `¿Estás seguro de eliminar al usuario "${userData.name}"? El usuario será desactivado.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await userService.delete(userData.id)
      toast.success('Usuario eliminado')
      navigate('/configuration/users')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Usuario no encontrado</p>
      </div>
    )
  }

  const isCurrentUser = userData.id === currentUser?.id

  const tabs: TabItem[] = [
    { label: 'Información', path: '', icon: User, end: true },
  ]

  const contextValue: UserContextType = {
    userData,
    isLoading,
    isEditing,
    isSaving,
    isCurrentUser,
    formData,
    setIsEditing,
    setFormData,
    handleSave,
    handleToggleActive,
    fetchUser,
  }

  return (
    <UserContext.Provider value={contextValue}>
      <DetailLayout
        title={userData.name}
        subtitle="Administrador"
        backPath="/configuration/users"
        basePath={`/configuration/users/${id}`}
        tabs={tabs}
        avatar={
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-2xl">
            {userData.name.charAt(0).toUpperCase()}
          </div>
        }
        badges={
          <>
            <span className={`badge ${userData.isActive ? 'badge-success' : 'badge-gray'}`}>
              {userData.isActive ? 'Activo' : 'Inactivo'}
            </span>
            {isCurrentUser && (
              <span className="badge badge-primary">Tú</span>
            )}
            {!isCurrentUser && (
              <button
                onClick={handleToggleActive}
                className={`btn btn-sm ${userData.isActive ? 'btn-secondary' : 'bg-success-50 text-success-700 hover:bg-success-100'}`}
              >
                <Power className="h-3.5 w-3.5 mr-1" />
                {userData.isActive ? 'Desactivar' : 'Activar'}
              </button>
            )}
          </>
        }
        actions={
          <>
            {!isEditing ? (
              <>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Key className="h-4 w-4" />
                  <span className="hidden sm:inline">Cambiar Contraseña</span>
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Editar</span>
                </button>
                {!isCurrentUser && (
                  <button
                    onClick={handleDelete}
                    className="btn btn-danger flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false)
                    setFormData({
                      name: userData.name,
                      email: userData.email || '',
                    })
                  }}
                  className="btn btn-secondary flex items-center gap-2"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={isSaving}
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            )}
          </>
        }
      >
        <Outlet />
      </DetailLayout>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Cambiar Contraseña
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Introduce la nueva contraseña para {userData.name}
            </p>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input pr-10"
                placeholder="Nueva contraseña (min. 8 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowPasswordModal(false)
                  setNewPassword('')
                  setShowPassword(false)
                }}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePassword}
                className="btn btn-primary"
                disabled={isSaving || newPassword.length < 8}
              >
                {isSaving ? 'Guardando...' : 'Cambiar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </UserContext.Provider>
  )
}
